import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getEmailField } from '@lender/utils';
import { Box } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import CheckboxCDS from '@sharedCR/coreDesignSystem/form/CheckboxCDS';
import DataFieldCDS from '@sharedCR/coreDesignSystem/form/DataFieldCDS';
import DateFieldCDS from '@sharedCR/coreDesignSystem/form/DateFieldCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { object, string } from 'yup';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import useForm from 'hooks/useForm';

import { selectLoanApplicationBorrowerOptions } from 'store/loanApplications/selectors';

import { INTERACTION_TYPE_OPTIONS } from '../../Servicing.constants';
import { ServiceLogTestIds } from '../ServiceLog.testIds';

const interactionSchema = object().shape({
  interactionDate: string().nullable().required('Interaction date is required'),
  interactionType: object().nullable().required('Interaction type is required'),
  subject: string().required('Subject is required'),
  description: string().required('Description is required'),
  contactPhone: string().matches(/^\d{10}$/, {
    message: 'Phone number must be 10 digits',
    excludeEmptyString: true,
  }),
  contactEmail: getEmailField({ isRequired: false }),
});

const OTHER_CONTACT_OPTION = { name: 'Other', value: 'other' };

const OBSERVABLE_FIELDS = [
  'interactionDate',
  'interactionType',
  'subject',
  'description',
  'contactSource',
  'contactName',
  'contactPhone',
  'contactEmail',
  'resolution',
  'followUpDate',
  'followUpCompleted',
];

const formFieldsStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '24px',
  overflowY: 'auto',
  height: '100%',
};

const getInitialFormValues = (interaction, borrowerOptions) => {
  const matchedBorrower = interaction?.contactName
    ? borrowerOptions.find(b => b.name === interaction.contactName)
    : null;

  return {
    interactionDate: interaction?.interactionDate ?? dayjs().format(COMMON_REQUEST_DATE_FORMAT),
    interactionType: interaction
      ? (INTERACTION_TYPE_OPTIONS.find(o => o.value === interaction.interactionType) ?? null)
      : null,
    subject: interaction?.subject ?? '',
    description: interaction?.description ?? '',
    contactSource: matchedBorrower ?? (interaction?.contactName ? OTHER_CONTACT_OPTION : null),
    contactName: interaction?.contactName ?? '',
    contactPhone: interaction?.contactPhone ?? '',
    contactEmail: interaction?.contactEmail ?? '',
    resolution: interaction?.resolution ?? '',
    followUpDate: interaction?.followUpDate ?? null,
    followUpCompleted: interaction?.followUpCompleted ?? false,
  };
};

const InteractionDrawerContent = ({
  setIsFormChanged,
  isSaving,
  setIsLoading,
  additionalProps: { interaction, onSave: onSaveCallback },
  setOnSave,
}) => {
  const borrowerOptions = useSelector(selectLoanApplicationBorrowerOptions);
  const isEditing = !!interaction;

  const contactOptions = useMemo(
    () => [...borrowerOptions, OTHER_CONTACT_OPTION],
    [borrowerOptions],
  );

  const initialFormValues = useMemo(
    () => getInitialFormValues(interaction, borrowerOptions),
    [interaction, borrowerOptions],
  );

  const {
    form,
    handleChange,
    isFormChanged,
    setInForm,
    setInitialFormValues,
    setForm,
    setSchema,
    validateForm,
    validationErrors,
  } = useForm(initialFormValues, {}, OBSERVABLE_FIELDS);

  useEffect(() => {
    setSchema(interactionSchema);
  }, [setSchema]);

  const isOtherContact = form?.contactSource?.value === OTHER_CONTACT_OPTION.value;

  useEffect(() => {
    setIsFormChanged(isFormChanged);
  }, [isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setInitialFormValues, setForm]);

  const onTypeChange = useCallback(
    (_, selectedOption) => {
      setInForm('interactionType', selectedOption);
    },
    [setInForm],
  );

  const onContactSourceChange = useCallback(
    (_, selectedOption) => {
      setInForm('contactSource', selectedOption);

      if (!selectedOption || selectedOption.value === OTHER_CONTACT_OPTION.value) {
        setInForm('contactName', '');
      }

      if (selectedOption && selectedOption.value !== OTHER_CONTACT_OPTION.value) {
        setInForm('contactName', selectedOption.name);
      }
    },
    [setInForm],
  );

  const onDateChange = useCallback(
    value => {
      setInForm('interactionDate', value);
    },
    [setInForm],
  );

  const onFollowUpDateChange = useCallback(
    value => {
      setInForm('followUpDate', value);
    },
    [setInForm],
  );

  const onFollowUpCompletedChange = useCallback(
    e => {
      setInForm('followUpCompleted', e.target.checked);
    },
    [setInForm],
  );

  const onSave = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      await onSaveCallback(form, isEditing);
    } finally {
      setIsLoading(false);
    }
  }, [form, isEditing, onSaveCallback, setIsLoading, validateForm]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  return (
    <Box sx={formFieldsStyles}>
      <DateFieldCDS
        required
        dataTestId={ServiceLogTestIds.INTERACTION_DATE}
        error={!!validationErrors.interactionDate}
        helperText={validationErrors.interactionDate}
        label="Interaction Date"
        name="interactionDate"
        value={form.interactionDate}
        onChange={onDateChange}
      />

      <AutocompleteCDS
        required
        dataTestId={ServiceLogTestIds.TYPE_SELECT}
        error={!!validationErrors.interactionType}
        helperText={validationErrors.interactionType}
        label="Interaction Type"
        name="interactionType"
        optionKey="value"
        optionName="name"
        options={INTERACTION_TYPE_OPTIONS}
        value={form.interactionType}
        onChange={onTypeChange}
      />

      <TextFieldCDS
        required
        dataTestId={ServiceLogTestIds.SUBJECT_INPUT}
        disabled={isSaving}
        error={!!validationErrors.subject}
        helperText={validationErrors.subject}
        label="Subject"
        name="subject"
        value={form.subject}
        onChange={handleChange}
      />

      <TextFieldCDS
        multiline
        required
        dataTestId={ServiceLogTestIds.DESCRIPTION_INPUT}
        disabled={isSaving}
        error={!!validationErrors.description}
        helperText={validationErrors.description}
        label="Description"
        name="description"
        rows={3}
        value={form.description}
        onChange={handleChange}
      />

      <AutocompleteCDS
        dataTestId={ServiceLogTestIds.CONTACT_SELECT}
        label="Contact"
        name="contactSource"
        optionKey="value"
        optionName="name"
        options={contactOptions}
        value={form.contactSource}
        onChange={onContactSourceChange}
      />

      {isOtherContact && (
        <TextFieldCDS
          dataTestId={ServiceLogTestIds.CONTACT_NAME_INPUT}
          disabled={isSaving}
          label="Contact Name"
          name="contactName"
          value={form.contactName}
          onChange={handleChange}
        />
      )}

      <DataFieldCDS
        disabled={isSaving}
        label="Contact Phone"
        name="contactPhone"
        type={FieldInputType.TEL}
        value={form.contactPhone}
        onChange={handleChange}
      />

      <TextFieldCDS
        dataTestId={ServiceLogTestIds.CONTACT_EMAIL_INPUT}
        disabled={isSaving}
        label="Contact Email"
        name="contactEmail"
        type="email"
        value={form.contactEmail}
        onChange={handleChange}
      />

      <TextFieldCDS
        multiline
        dataTestId={ServiceLogTestIds.RESOLUTION_INPUT}
        disabled={isSaving}
        label="Resolution"
        name="resolution"
        rows={3}
        value={form.resolution}
        onChange={handleChange}
      />

      <DateFieldCDS
        dataTestId={ServiceLogTestIds.FOLLOW_UP_DATE}
        label="Follow-Up Date"
        name="followUpDate"
        value={form.followUpDate}
        onChange={onFollowUpDateChange}
      />

      <CheckboxCDS
        dataTestId={ServiceLogTestIds.FOLLOW_UP_CHECKBOX}
        label="Follow-Up Completed"
        name="followUpCompleted"
        value={form.followUpCompleted}
        onChange={onFollowUpCompletedChange}
      />
    </Box>
  );
};

InteractionDrawerContent.propTypes = {
  additionalProps: PropTypes.shape({
    onSave: PropTypes.func.isRequired,
    interaction: PropTypes.object,
  }).isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default memo(InteractionDrawerContent);
